import {StyleSheet, View} from 'react-native'

import {type FlashBookDesign} from '#/themes-flashbook/design'

/**
 * The decorative layer of a FlashBook page.
 *
 * These are the pieces that make a page read as *paper* instead of a coloured
 * rectangle: the ruling of an exercise book, the graph grid of a maths sheet,
 * the starfield of a mission briefing, the column rules of an editorial spread.
 *
 * Everything is drawn with plain Views — no images, no SVG, no font glyphs — so
 * it costs nothing to load, scales to any page size, and never blocks the text
 * it sits behind. Textures are `pointerEvents="none"` and low-opacity by
 * construction: decoration must never compete with the content on top of it.
 */

/** Deterministic star field — a fixed table keeps renders stable. */
const STARS: {x: number; y: number; r: number; o: number}[] = [
  {x: 0.08, y: 0.05, r: 1.4, o: 0.7},
  {x: 0.31, y: 0.02, r: 1, o: 0.45},
  {x: 0.62, y: 0.07, r: 1.8, o: 0.85},
  {x: 0.88, y: 0.03, r: 1.1, o: 0.5},
  {x: 0.18, y: 0.16, r: 1, o: 0.4},
  {x: 0.74, y: 0.19, r: 1.5, o: 0.65},
  {x: 0.45, y: 0.27, r: 1, o: 0.35},
  {x: 0.93, y: 0.31, r: 1.3, o: 0.55},
  {x: 0.05, y: 0.38, r: 1.6, o: 0.6},
  {x: 0.56, y: 0.44, r: 1, o: 0.3},
  {x: 0.27, y: 0.52, r: 1.4, o: 0.5},
  {x: 0.82, y: 0.57, r: 1.1, o: 0.4},
  {x: 0.11, y: 0.64, r: 1.7, o: 0.6},
  {x: 0.66, y: 0.71, r: 1, o: 0.32},
  {x: 0.38, y: 0.79, r: 1.5, o: 0.55},
  {x: 0.9, y: 0.84, r: 1.2, o: 0.42},
  {x: 0.22, y: 0.91, r: 1.6, o: 0.5},
  {x: 0.71, y: 0.96, r: 1, o: 0.3},
]

/** Paper ruling: the pitch comes from the theme's own body line height. */
export const DEFAULT_RULED_STEP = 28
/** Graph paper square size. */
export const GRID_STEP = 24

/** Textures that are a *ruling* — lines the text is written on. */
export function isRulingTexture(
  texture: FlashBookDesign['page']['texture'],
): boolean {
  return texture === 'ruled' || texture === 'grid'
}

/**
 * The ruled lines of a page — the lines the reader writes on.
 *
 * These are kept apart from the page's *field* texture (the warm wash, the
 * starfield, the editorial column rules) because they have to move and align
 * with the text: a ruling that sits still behind scrolling text strikes through
 * it the moment the reader scrolls. `FlashBookPageRuling` is therefore rendered
 * inside the scrolling content, at the text's own line pitch and phase.
 */
function RulingLines({
  texture,
  color,
  width,
  height,
  step,
  offset,
  inset,
  rtl = false,
}: {
  texture: FlashBookDesign['page']['texture']
  color: string
  width: number
  height: number
  /** Vertical distance between two rules. */
  step: number
  /** Where the first rule falls, measured from the top of the content. */
  offset: number
  /** The page's own padding — lets the margin rule sit *in* the margin. */
  inset: number
  /** Whether the writing runs right-to-left, which flips the margin rule. */
  rtl?: boolean
}) {
  if (width <= 0 || height <= 0 || step <= 0) return null

  if (texture === 'ruled') {
    const lines = Math.ceil((height - offset) / step)
    return (
      <>
        {Array.from({length: Math.max(0, lines)}, (_, i) => (
          <View
            key={`r${i}`}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              // Written *on* the line: each rule closes the text line above it.
              top: offset + i * step,
              height: StyleSheet.hairlineWidth * 2,
              backgroundColor: color,
            }}
          />
        ))}
        {/* Margin rule — the line down the side of an exercise book. It is
            placed *inside* the page padding so it runs through the margin and
            never strikes through the first word of every line.

            Which side is the margin is a property of the *book*: an exercise
            book written in Arabic has its margin on the right, and the rule
            has to be there or the page reads as an English notebook with its
            writing right-aligned. */}
        <View
          style={{
            position: 'absolute',
            top: 0,
            height,
            ...(rtl
              ? {right: Math.max(6, inset - 7)}
              : {left: Math.max(6, inset - 7)}),
            width: StyleSheet.hairlineWidth * 2,
            backgroundColor: color,
          }}
        />
      </>
    )
  }

  const rows = Math.ceil(height / step)
  const cols = Math.ceil(width / step)
  return (
    <>
      {Array.from({length: rows}, (_, i) => (
        <View
          key={`gr${i}`}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: offset + i * step,
            height: StyleSheet.hairlineWidth,
            backgroundColor: color,
          }}
        />
      ))}
      {Array.from({length: cols}, (_, i) => (
        <View
          key={`gc${i}`}
          style={{
            position: 'absolute',
            top: 0,
            height,
            left: (i + 1) * step,
            width: StyleSheet.hairlineWidth,
            backgroundColor: color,
          }}
        />
      ))}
    </>
  )
}

function FieldLines({
  texture,
  color,
}: {
  texture: FlashBookDesign['page']['texture']
  color: string
}) {
  if (texture === 'stars') {
    return (
      <>
        {STARS.map((s, i) => (
          <View
            key={`s${i}`}
            style={{
              position: 'absolute',
              left: `${s.x * 100}%`,
              top: `${s.y * 100}%`,
              width: s.r * 2,
              height: s.r * 2,
              borderRadius: s.r,
              backgroundColor: color,
              opacity: s.o,
            }}
          />
        ))}
      </>
    )
  }

  if (texture === 'column') {
    return (
      <>
        {[0.34, 0.67].map((x, i) => (
          <View
            key={`c${i}`}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${x * 100}%`,
              width: StyleSheet.hairlineWidth,
              backgroundColor: color,
            }}
          />
        ))}
      </>
    )
  }

  // Warm paper: one edge-free wash. Bands were tried first and they read as a
  // hard seam across the middle of the page — the page must look dyed, not
  // striped, so this is a single full-height overlay with no boundaries at all.
  if (texture === 'paper') {
    return (
      <View
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          backgroundColor: color,
        }}
      />
    )
  }

  return null
}

/**
 * Fills its (relatively positioned) parent with the theme's *field* texture:
 * the warm wash of paper, the starfield of a mission briefing, the column rules
 * of editorial stock. Rulings are handled by `FlashBookPageRuling`, which has
 * to know the text's line pitch.
 *
 * A no-op for `plain` and for the ruling textures, so a page can safely mount
 * both and let the theme decide which one it actually prints.
 */
export function FlashBookPageTexture({
  texture,
  color,
}: {
  texture: FlashBookDesign['page']['texture']
  color: string
}) {
  if (texture === 'plain' || isRulingTexture(texture)) return null

  // Column rules are a whisper of editorial stock, not a grid: at anything
  // approaching full strength the two hairlines slice straight through the body
  // text and read as a rendering fault rather than a page texture.
  const baseOpacity =
    texture === 'paper' ? 0.07 : texture === 'column' ? 0.1 : 0.9

  return (
    <View
      pointerEvents="none"
      // zIndex 0 keeps the paper under the page content on web, where an
      // absolutely positioned sibling would otherwise paint over it.
      style={[StyleSheet.absoluteFill, {opacity: baseOpacity, zIndex: 0}]}>
      <FieldLines texture={texture} color={color} />
    </View>
  )
}

/**
 * The theme's ruling — the lines the page's text is written on.
 *
 * Rendered *inside* the scrolling content, with an explicit size, for two
 * reasons:
 *
 *  1. **Alignment.** The pitch is the theme's own body line height and the
 *     phase starts at the page padding, so a rule closes the text line above
 *     it: the page reads as an exercise book where the writing sits on the
 *     lines, instead of a drifting grid that strikes through the glyphs.
 *  2. **It scrolls with the text.** A ruling pinned to the frame would slide
 *     out of register the moment the reader scrolls a long page.
 *
 * The caller supplies the measured content box because the content is what
 * defines it — there is nothing to measure inside this component.
 */
export function FlashBookPageRuling({
  texture,
  color,
  width,
  height,
  step,
  offset,
  inset,
  opacity = 0.16,
  rtl = false,
}: {
  texture: FlashBookDesign['page']['texture']
  color: string
  width: number
  height: number
  step: number
  offset: number
  /** The page's padding, so the margin rule sits *in* the margin. */
  inset: number
  opacity?: number
  /** Whether the writing runs right-to-left: the margin rule changes sides. */
  rtl?: boolean
}) {
  if (!isRulingTexture(texture)) return null
  if (width <= 0 || height <= 0) return null

  return (
    <View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, {opacity, zIndex: 0}]}>
      <RulingLines
        texture={texture}
        color={color}
        width={width}
        height={height}
        step={step}
        offset={offset}
        inset={inset}
        rtl={rtl}
      />
    </View>
  )
}

/**
 * The mark that opens a page — a chapter rule, a masthead ribbon, a mission
 * star. Sits above the page title, never beside it.
 */
export function FlashBookPageOrnament({
  ornament,
  color,
}: {
  ornament: FlashBookDesign['page']['ornament']
  color: string
}) {
  if (ornament === 'none') return null

  if (ornament === 'chapter-rule') {
    return (
      <View style={od.chapterRule} pointerEvents="none">
        <View style={[od.rule, {backgroundColor: color, opacity: 0.5}]} />
        <View style={[od.diamond, {backgroundColor: color, opacity: 0.7}]} />
        <View style={[od.rule, {backgroundColor: color, opacity: 0.5}]} />
      </View>
    )
  }

  if (ornament === 'stars') {
    return (
      <View style={od.starRow} pointerEvents="none">
        {[5, 3, 2].map((s, i) => (
          <View
            key={i}
            style={{
              width: s,
              height: s,
              borderRadius: s / 2,
              backgroundColor: color,
              opacity: 0.75,
            }}
          />
        ))}
      </View>
    )
  }

  // A squared-paper mark: a small graph swatch, the way a workbook flags a
  // page. Two bare dashes were tried first and read as a stray artifact rather
  // than a deliberate page mark.
  if (ornament === 'grid') {
    return (
      <View
        style={[od.gridMark, {borderColor: color, opacity: 0.6}]}
        pointerEvents="none">
        <View style={[od.gridMarkLine, {backgroundColor: color}]} />
        <View
          style={[
            od.gridMarkLine,
            od.gridMarkLineVert,
            {backgroundColor: color},
          ]}
        />
      </View>
    )
  }

  if (ornament === 'ribbon') {
    return (
      <View style={od.ribbon} pointerEvents="none">
        <View
          style={{
            height: 3,
            width: 30,
            backgroundColor: color,
            opacity: 0.9,
          }}
        />
        <View
          style={{
            height: StyleSheet.hairlineWidth * 2,
            width: 30,
            backgroundColor: color,
            opacity: 0.4,
          }}
        />
      </View>
    )
  }

  // dots
  return (
    <View style={od.dotRow} pointerEvents="none">
      {[0, 1, 2].map(i => (
        <View
          key={i}
          style={{
            width: 3,
            height: 3,
            borderRadius: 1.5,
            backgroundColor: color,
            opacity: 0.7,
          }}
        />
      ))}
    </View>
  )
}

/**
 * The break between two blocks. `ornament` is the storybook flourish, `center-dot`
 * the mission divider, `double-rule` the editorial section break.
 */
export function FlashBookDivider({
  divider,
  color,
  opacity,
  /** Vertical breathing room, so the host can centre the rule in its gap. */
  pad = 10,
}: {
  divider: FlashBookDesign['block']['divider']
  color: string
  opacity: number
  pad?: number
}) {
  if (divider === 'none') return null
  const line = {backgroundColor: color, opacity}

  if (divider === 'ornament') {
    return (
      <View style={[dv.wrap, {paddingVertical: pad}]} pointerEvents="none">
        <View style={[dv.line, line]} />
        <View
          style={[dv.dot, {backgroundColor: color, opacity: opacity + 0.15}]}
        />
        <View style={[dv.line, line]} />
      </View>
    )
  }

  if (divider === 'center-dot') {
    return (
      <View style={[dv.wrap, {paddingVertical: pad}]} pointerEvents="none">
        <View style={[dv.line, line]} />
        <View
          style={[dv.dot, {backgroundColor: color, opacity: opacity + 0.15}]}
        />
        <View style={[dv.line, line]} />
      </View>
    )
  }

  if (divider === 'double-rule') {
    return (
      <View
        style={[dv.doubleWrap, {paddingVertical: pad}]}
        pointerEvents="none">
        <View style={[dv.fullLine, line, {height: 2}]} />
        <View
          style={[
            dv.fullLine,
            line,
            {height: StyleSheet.hairlineWidth, marginTop: 3},
          ]}
        />
      </View>
    )
  }

  // hairline
  return (
    <View style={[dv.hairWrap, {paddingVertical: pad}]} pointerEvents="none">
      <View
        style={[
          dv.fullLine,
          {
            backgroundColor: color,
            opacity,
            height: StyleSheet.hairlineWidth * 2,
          },
        ]}
      />
    </View>
  )
}

const od = StyleSheet.create({
  chapterRule: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingBottom: 10,
  },
  rule: {height: 1, width: 26},
  diamond: {width: 5, height: 5, transform: [{rotate: '45deg'}]},
  starRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingBottom: 10,
  },
  gridMark: {
    width: 20,
    height: 20,
    borderWidth: StyleSheet.hairlineWidth * 2,
    marginBottom: 10,
    justifyContent: 'center',
  },
  gridMarkLine: {position: 'absolute', left: 0, right: 0, top: 9, height: 1},
  gridMarkLineVert: {
    left: 9,
    right: undefined,
    top: 0,
    bottom: 0,
    height: undefined,
    width: 1,
  },
  ribbon: {gap: 3, paddingBottom: 10},
  dotRow: {flexDirection: 'row', gap: 4, paddingBottom: 10},
})

const dv = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
  },
  line: {flex: 1, height: StyleSheet.hairlineWidth * 2},
  dot: {width: 4, height: 4, borderRadius: 2},
  doubleWrap: {paddingVertical: 10},
  fullLine: {width: '100%'},
  hairWrap: {paddingVertical: 8},
  hair: {width: '100%'},
})
